import { Box, Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  BidV2Status,
  BidV2StatusTransitionTypeDto,
  type BuyerBidV2DetailDto,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { latestTransition } from "./bidActivity";
import type { BidEvaluationSummary } from "./bidEvaluationSummary";
import { BidQualificationDecision } from "./BidQualificationDecision";
import { RejectBidButton } from "./BidRejectionActionCard";

interface Props {
  detail: BuyerBidV2DetailDto;
  summary: BidEvaluationSummary;
  awarded: boolean;
  onMarkWinner: () => void;
  canSelectWinner: boolean;
}

export const BidActionsCard = ({
  detail,
  summary,
  awarded,
  onMarkWinner,
  canSelectWinner,
}: Props) => {
  if (detail.bid.status === BidV2Status.Rejected) {
    return null;
  }
  if (detail.bid.status === BidV2Status.Awarded) {
    return <AwardedBanner detail={detail} />;
  }
  if (awarded) {
    return <NotSelectedBanner />;
  }
  return (
    <ActiveActions
      detail={detail}
      summary={summary}
      onMarkWinner={onMarkWinner}
      canSelectWinner={canSelectWinner}
    />
  );
};

const NotSelectedBanner = () => {
  const { t } = useTranslation();
  return (
    <Box
      bg="smBackgroundSecondary"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Stack spacing="0.5">
        <Text fontWeight="semibold" color="smText">
          {t("Not selected")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("This bid was not selected for award, but it was not rejected.")}
        </Text>
      </Stack>
    </Box>
  );
};

const AwardedBanner = ({ detail }: { detail: BuyerBidV2DetailDto }) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const awardedAt = latestTransition(
    detail.bid.statusHistory,
    BidV2StatusTransitionTypeDto.Awarded,
  )?.at;
  return (
    <Box
      bg="green.50"
      border="1px solid"
      borderColor="green.200"
      rounded="xl"
      px="5"
      py="4"
      color="green.800"
    >
      <Stack spacing="0.5">
        <Text fontWeight="semibold">{t("Selected as winner")}</Text>
        {awardedAt && (
          <Text fontSize="sm" opacity={0.85}>
            {displayDateTime(awardedAt)}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

const ActiveActions = ({
  detail,
  summary,
  onMarkWinner,
  canSelectWinner,
}: {
  detail: BuyerBidV2DetailDto;
  summary: BidEvaluationSummary;
  onMarkWinner: () => void;
  canSelectWinner: boolean;
}) => {
  const { t } = useTranslation();
  const showRejectButton = detail.bid.status === BidV2Status.Submitted;
  // The button only navigates to the award tab, which refuses to select a winner before the
  // evaluation is completed — offering it earlier would point at a screen that will not act on it.
  const showWinnerButton = canSelectWinner && summary.state === "evaluated" && summary.rank === 1;
  if (!showRejectButton && !showWinnerButton) {
    return null;
  }

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="wider"
        textTransform="uppercase"
        color="smMuted"
        mb="3"
      >
        {t("Actions")}
      </Text>
      <Stack spacing="3">
        {/* Renders itself only for procedures that qualify at the tender stage. */}
        <BidQualificationDecision detail={detail} />
        {showRejectButton && <RejectBidButton detail={detail} />}
        {showWinnerButton && (
          <Button colorScheme="teal" onClick={onMarkWinner} width="full">
            {t("Mark as winner")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
